import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ArrowLeft, ArrowLeftRight, Check, ChevronDown, FileSearch, LayoutGrid, Map as MapaIcone, Plus } from 'lucide-react';
import { useConsultasInventario } from '@/hooks/inventario';
import { cn } from '@/lib/utils';

/**
 * A barra do inventário: igual em todas as telas do módulo.
 *
 * O PBS espalha o inventário por oito formulários e seis consultas, cada um
 * aberto pelo menu e fechado para abrir o próximo. Aqui tudo do inventário
 * fica a um clique de qualquer tela dele — etapas, montar escopo e cada
 * consulta — e a consulta abre já no escopo em que se estava.
 *
 * As consultas ficam num menu, e não em abas soltas: seis títulos longos do
 * PBS lado a lado não cabem na barra e obrigavam a rolar de lado.
 */
export function NavInventario({
  inventario,
  nome,
  escopo,
  podePlanejar,
}: {
  inventario: number;
  nome: string;
  /** Escopo em foco: as consultas abrem filtradas por ele. */
  escopo?: number | null | undefined;
  podePlanejar: boolean;
}): JSX.Element {
  const consultas = useConsultasInventario();
  const local = useLocation();
  const navegar = useNavigate();
  const base = `/inventario/${inventario}`;
  const sufixo = escopo ? `?escopo=${escopo}` : '';
  const lista = consultas.data?.consultas ?? [];

  const caminho = decodeURIComponent(local.pathname);
  const atual = lista.find((c) => caminho === `${base}/consulta/${c.consulta}`) ?? null;

  const aba = (ativa: boolean): string =>
    cn(
      'inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium transition-colors',
      ativa ? 'bg-primaria text-white shadow-xs' : 'text-textoFraco hover:bg-superficie2 hover:text-texto',
    );

  return (
    <nav className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2 text-[13px] text-textoFraco">
        <NavLink to="/inventario" className="inline-flex shrink-0 items-center gap-1 hover:text-texto">
          <ArrowLeft className="h-4 w-4" />
          Inventários
        </NavLink>
        <span>/</span>
        <span className="truncate font-medium text-texto">{nome}</span>
      </div>

      <div className="flex items-center gap-1 rounded-xl border border-borda bg-superficie p-1 shadow-xs">
        <NavLink to={base} end className={({ isActive }) => aba(isActive)}>
          <LayoutGrid className="h-4 w-4" />
          Etapas
        </NavLink>
        <NavLink to={`${base}/mapa`} className={({ isActive }) => aba(isActive)}>
          <MapaIcone className="h-4 w-4" />
          Mapa
        </NavLink>
        <NavLink to={`${base}/antes-depois`} className={({ isActive }) => aba(isActive)}>
          <ArrowLeftRight className="h-4 w-4" />
          Antes × depois
        </NavLink>
        {podePlanejar ? (
          <NavLink to={`${base}/escopo/novo`} className={({ isActive }) => aba(isActive)}>
            <Plus className="h-4 w-4" />
            Novo escopo
          </NavLink>
        ) : null}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button type="button" className={cn(aba(atual !== null), 'max-w-[260px]')}>
              <FileSearch className="h-4 w-4 shrink-0" />
              <span className="truncate">{atual ? encurtar(atual.descricao) : 'Consultas'}</span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-50 w-80 overflow-hidden rounded-xl border border-borda bg-superficie p-1.5 shadow-forte data-[state=open]:animate-escalar"
            >
              <p className="px-2.5 pb-1.5 pt-1 text-2xs font-semibold uppercase tracking-wide text-textoSuave">
                Consultas do PBS{escopo ? ` · escopo ${escopo}` : ''}
              </p>
              {lista.length === 0 ? (
                <p className="px-2.5 py-2 text-[13px] text-textoSuave">Carregando…</p>
              ) : (
                lista.map((c) => {
                  const ativa = atual?.consulta === c.consulta;
                  return (
                    <DropdownMenu.Item
                      key={c.consulta}
                      onSelect={() => navegar(`${base}/consulta/${encodeURIComponent(c.consulta)}${sufixo}`)}
                      className={cn(
                        'flex min-h-[38px] cursor-pointer items-center gap-2 rounded-lg px-2.5 text-[13px] data-[highlighted]:bg-superficie2 data-[highlighted]:outline-none',
                        ativa && 'font-semibold text-primaria',
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">{c.descricao}</span>
                      {ativa ? <Check className="h-4 w-4 shrink-0" /> : null}
                    </DropdownMenu.Item>
                  );
                })
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </nav>
  );
}

/** "Inventário Diferencas da 1ª Contagem" -> "Diferencas da 1ª Contagem". */
function encurtar(descricao: string): string {
  return descricao.replace(/^Invent[aá]rio\s+/i, '');
}
